import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { DspAdgroup, DspCreative, DspModerationStatus, DspRejectedAdInfo } from "@front/m19-api-client";
import {
  IAccordionComponent,
  IBadgeComponent,
  IIconComponent,
  ITableBodyDirective,
  ITableDirective,
  ITableHeadDirective,
  ITableRowDirective,
  ITableTdDirective,
  ITableThDirective,
  MODAL_DATA,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";

export interface DspRejectedAdView {
  adGroupName: string;
  creativeName: string;
}

export interface DspRejectedVerdictView {
  moderationStatus: DspModerationStatus;
  description?: string;
  policyUrl?: string;
  ads: DspRejectedAdView[];
}

export interface DspRejectedAdsModalData {
  verdicts: DspRejectedVerdictView[];
}

function moderationRank(status: DspModerationStatus): number {
  return status === DspModerationStatus.NOT_APPROVED ? 0 : 1;
}

export function toRejectedVerdicts(
  rejectedAds: DspRejectedAdInfo[],
  strategyId: number,
  adGroups: DspAdgroup[],
  creatives: DspCreative[],
): DspRejectedVerdictView[] {
  const adGroupNames = new Map(adGroups.map((adGroup) => [adGroup.adGroupId, adGroup.name]));
  const creativeNames = new Map(creatives.map((creative) => [creative.adCreativeId, creative.name]));
  return rejectedAds
    .filter((rejectedAd) => rejectedAd.strategyId === strategyId)
    .sort((a, b) => moderationRank(a.moderationStatus) - moderationRank(b.moderationStatus))
    .map((rejectedAd) => ({
      moderationStatus: rejectedAd.moderationStatus,
      description: rejectedAd.description,
      policyUrl: rejectedAd.policyUrl,
      ads: rejectedAd.ads
        .map((ad) => ({
          adGroupName: adGroupNames.get(ad.adGroupId) ?? ad.adGroupId,
          creativeName: creativeNames.get(ad.creativeId) ?? ad.creativeId,
        }))
        .sort((a, b) => a.adGroupName.localeCompare(b.adGroupName) || a.creativeName.localeCompare(b.creativeName)),
    }));
}

@Component({
  selector: "app-dsp-rejected-ads-modal",
  imports: [
    IAccordionComponent,
    IBadgeComponent,
    IIconComponent,
    ITableBodyDirective,
    ITableDirective,
    ITableHeadDirective,
    ITableRowDirective,
    ITableTdDirective,
    ITableThDirective,
    TranslocoDirective,
  ],
  templateUrl: "./dsp-rejected-ads-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspRejectedAdsModalComponent {
  readonly verdicts = inject<DspRejectedAdsModalData>(MODAL_DATA).verdicts;
  readonly DspModerationStatus = DspModerationStatus;

  private readonly openVerdicts = signal<ReadonlySet<number>>(new Set());

  isOpen(index: number): boolean {
    return this.openVerdicts().has(index);
  }

  toggle(index: number): void {
    this.openVerdicts.update((open) => {
      const toggled = new Set(open);
      if (!toggled.delete(index)) {
        toggled.add(index);
      }
      return toggled;
    });
  }
}
